import { AnimateIn } from '@oysterjs/ui/transition';

import {
  effortLevelLabel,
  FALLBACK_EFFORT_LEVEL
} from '@oysterjs/ais/components/presets/effort-level';

/**
 * Shown when the server ran the workspace at a higher effort than was asked
 * for: a LOW workspace whose uploads would not fit in one pass, or a Quick
 * workspace that Cara moved onto a more capable model partway through.
 *
 * It says that the level changed but not why. The reason the server records
 * is for support, and is deliberately not shown to the customer. It is drawn in
 * the page's own gray for the same reason: it records the change rather than
 * calling attention to it.
 *
 * It is a notice rather than a chat message because the model must not see it:
 * the conversation is replayed into the prompt, and a line about how the run was
 * configured invites the model to talk about its own configuration in the
 * answer.
 *
 * `withinQuick` is the second case. The workspace is still labelled Quick, so the
 * copy speaks of more power rather than a level, and says it holds for every
 * later message.
 */
export const EffortRaisedNotice = ({ withinQuick = false }: { withinQuick?: boolean }) => {
  const raisedTo = `${effortLevelLabel(FALLBACK_EFFORT_LEVEL)} analysis`;
  return (
    <AnimateIn
      data-conversation-role="effort-raised-notice"
      // Announced, because it appears partway through a run the user already
      // started: without a live region the only way to learn Cara changed the
      // setting is to navigate back over the transcript looking for it.
      role="status"
      aria-live="polite"
      className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-100 dark:bg-neutral-700 px-4 py-3"
    >
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {withinQuick ? 'Added more power for this task' : `Switched to ${raisedTo}`}
      </p>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        {withinQuick
          ? 'Cara is putting more power behind this larger task. The extra power applies to this message and every one after it, so replies may take a little longer.'
          : 'Cara adjusted this workspace to give your request the attention it needs, so it may take a little longer to finish.'}
      </p>
    </AnimateIn>
  );
};
